// todo remove these material ui components
import * as Sentry from "@sentry/react";
import classNames from "classnames";
// files
import { Form, Formik } from "formik";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ampli } from "../../../../ampli";
// components
import PrimaryButton from "../../../../components/common/PrimaryButton";
import RadioButton from "../../../../components/common/RadioButton";
import Spinner from "../../../../components/common/Spinner";
import Alert, { AlertType } from "../../../../components/common/WaffyAlert";
// types
import { IUserLoginResponse } from "../../../../setup/api/services/auth/models";
import {
  requestOtp2,
  validateOtpGeneric,
} from "../../../../setup/api/services/otp";
import { EOTPType } from "../../../../setup/api/services/otp/models";
import { getProfileByPhone } from "../../../../setup/api/services/user";
import { IRegisterByPhoneResponseData } from "../../../../setup/api/services/user/models";
// apis
import { loginUser } from "../../../../setup/context/api";
// providers and context
import {
  contextUserLogin,
  fetchUserDependencies,
  setUserProfileInfo,
} from "../../../../setup/context/authContext";
import {
  LOGIN_RESPONSE_ERROR,
  OTP_FORM,
  PHONE_NUMBER_FORM,
  RESET_OTP_FORM,
  USER_DEACTIVATED_RESPONSE_ERROR,
} from "../../constants";
import { ILoginFormProps } from "../../models";
import { OtpCodeForm } from "../../otpCode/otpCodeForm";
import { PhoneNumberForm } from "../../phoneNumber/PhoneNumberForm";
import { SecurityCodeForm } from "../../securityPinCode/securityCodeForm";
import { ERROR_OTP_VALIDATION } from "../../signup/constants";
import {
  validateOtpCode,
  validatePhoneNumber,
  validateSecurityPinCode,
} from "../../validation";
import { ERegionCode } from "../../validation/enum";
import FormHeading from "../formHeading";
import FormHeadingContract from "../formHeadingContract";
import formModel from "./formModel";
import initialValues from "./initialValues";
import styles from "./loginForm.module.scss";

const LoginForm = ({
  setIsActualUser,
  publicContractLogin,
  authAction,
  onRegisterPhoneNumber,
  setNonCompletedUser,
  showOnboardingToggle,
  registeredUser,
}: ILoginFormProps) => {
  const { formId, formField } = formModel;
  const steps = {
    phoneNumber: "phoneNumber",
    otpCode: "otpCode",
    pinCode: "pincode",
  };
  const { t } = useTranslation();

  const [error, setError] = useState<string>();
  const [currentStep, setCurrentStep] = useState(steps.phoneNumber);
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [securityCode, setSecurityCode] = useState<string>("");
  const [otpCode, setOtpCode] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [profile, setProfile] = useState<IRegisterByPhoneResponseData>();
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  const [otpProvider, setOtpProvider] = useState<"WHATSAPP" | "SMS">("SMS");

  const _handleSubmit = () => {
    if (currentStep === steps.phoneNumber) onSubmitPhoneNumber();
    else if (currentStep === steps.otpCode) onOtpCodeFormSubmit();
    else onSubmitSecurityCode();
  };

  useEffect(() => {
    if (currentStep === steps.phoneNumber) {
      showOnboardingToggle && showOnboardingToggle(true);
    } else {
      showOnboardingToggle && showOnboardingToggle(false);
    }
  }, [currentStep]);

  const login = (formData: FormData) => {
    setLoading(true);
    loginUser(formData)
      .then(async (res: IUserLoginResponse) => {
        sendAmpliUserProperties(res);
        contextUserLogin(res);
        fetchUserDependencies();
        setLoading(false);
        authAction();
      })
      .catch((error) => {
        Sentry.captureException(error);
        setError(LOGIN_RESPONSE_ERROR);
        setLoading(false);
      });
  };
  const sendAmpliUserProperties = (res: IUserLoginResponse) => {
    if (res.user?.id) {
      try {
        ampli?.client?.setUserId(res.user.id);
        ampli?.client?.setUserProperties({
          userId: res.user?.id,
          userPhoneNumber: res.user.phoneNumber,
        });
      } catch (error) {
        Sentry.captureException(error);
        console.error(error);
      }
    }
  };
  const onSubmitPhoneNumber = () => {
    setLoading(true);
    if (validatePhoneNumber(`+${phoneNumber}`, ERegionCode.SA)) {
      ampli &&
        ampli?.submitPhoneNumber({
          userPhoneNumber: `+${phoneNumber}`,
          otpMethod: otpProvider.toLocaleLowerCase(),
        });
      getProfileByPhone({ phoneNumber: `+${phoneNumber}` })
        .then(async (result) => {
          try {
            if (
              result?.data?.enabled === false &&
              result?.data?.profile &&
              result?.data?.profile?.pinCodeEnabled === true
            ) {
              setError(USER_DEACTIVATED_RESPONSE_ERROR);
              setLoading(false);
              return;
            }
            result?.data && setProfile(result?.data);
            result?.data && setNonCompletedUser(result?.data);
            result?.data?.profile && setUserProfileInfo(result?.data?.profile);
            if (!result?.data?.profile?.pinCodeEnabled) {
              setIsActualUser(false);
            } else {
              setIsActualUser(true);
            }
            try {
              const body = {
                phoneNumber: `+${phoneNumber}`,
                notificationChannel: otpProvider,
              };
              await requestOtp2(body);
            } catch (error) {
              Sentry.captureException(error);
              console.error(error);
            }
            setLoading(false);
            setCurrentStep(steps.otpCode);
            setError(undefined);
          } catch (error) {
            Sentry.captureException(error);
            setLoading(false);
            console.error(error);
          }
        })
        .catch((err) => {
          Sentry.captureException(err);
          if (err.status === 404) {
            onRegisterPhoneNumber(phoneNumber, otpProvider);
            setIsActualUser(false);
          } else {
            setError(PHONE_NUMBER_FORM.ERROR);
          }
          setLoading(false);
        });
    } else {
      setLoading(false);
      setError(PHONE_NUMBER_FORM.ERROR);
    }
  };

  const onOtpCodeFormSubmit = () => {
    if (profile && otpCode && validateOtpCode(otpCode)) {
      validateOtpGeneric({
        type: EOTPType.WAFFY_OTP,
        otp: Number.parseInt(otpCode),
        phoneNumber: `+${phoneNumber}`,
      })
        .then((res) => {
          ampli && ampli?.validatedOtp({ isOtpValid: res?.data?.valid });
          if (res.data.valid) {
            setCurrentStep(steps.pinCode);
            setPhoneVerified(true);
          } else setError(ERROR_OTP_VALIDATION);
        })
        .catch((error) => {
          Sentry.captureException(error);
          setError(OTP_FORM.ERROR);
        });
    } else {
      setError(ERROR_OTP_VALIDATION);
    }
  };

  const onSubmitSecurityCode = () => {
    if (phoneVerified && validateSecurityPinCode(securityCode)) {
      const formData = new FormData();
      formData.append("username", `+${phoneNumber}`);
      formData.append("password", securityCode);
      login(formData);
    } else {
      setError(t("lbl_security_code_form_sub_title"));
    }
  };

  const loginBtn = () => {
    return (
      <div className={styles.login_button}>
        <PrimaryButton
          id={`login_${currentStep}`}
          disabled={phoneNumber === "" || loading || isDisabled}
          type="submit">
          {loading ? (
            <Spinner />
          ) : (
            `${
              currentStep === steps.otpCode
                ? t("lbl_confirm")
                : publicContractLogin
                ? t("lbl_contract_login_btn")
                : t("lbl_login")
            }`
          )}
        </PrimaryButton>
      </div>
    );
  };

  const checkError = useCallback((value: boolean) => {
    setIsDisabled(!value);
  }, []);

  return (
    <div
      className={classNames(
        styles["login_form_container"],
        publicContractLogin ? styles["public_contract"] : styles["public_login"]
      )}>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        onSubmit={_handleSubmit}>
        <Form className={styles["login_form"]} id={formId}>
          <div className={styles["login_form__section"]}>
            {error && (
              <Alert type={AlertType.ERROR} showIcon>
                {error}
              </Alert>
            )}

            {currentStep === steps.pinCode && (
              <>
                <FormHeading
                  header={t("lbl_otp_form_title")}
                  subHeader={t("lbl_security_code_form_sub_title")}
                  type={"pin"}
                  showReset={true}
                />
                <SecurityCodeForm onChange={setSecurityCode} />
              </>
            )}
            {currentStep === steps.otpCode && (
              <>
                <FormHeading
                  header={PHONE_NUMBER_FORM.TITLE}
                  subHeader={`${RESET_OTP_FORM.SUB_TITLE} ${phoneNumber}+`}
                  showReset={true}
                  showTimer={true}
                  type={"otp"}
                  registeredUser={registeredUser}>
                  <OtpCodeForm value={otpCode} key={0} onChange={setOtpCode} />
                </FormHeading>
              </>
            )}
            {currentStep === steps.phoneNumber ? (
              <>
                <span
                  className={publicContractLogin ? "publicContractLogin" : ""}>
                  <>
                    {publicContractLogin ? (
                      <div className={styles.public_contract_login}>
                        <FormHeadingContract />
                      </div>
                    ) : (
                      <FormHeading
                        header={t("lbl_welcome_login")}
                        subHeader={t("lbl_phone_number_form_sub_title")}
                        showReset={false}
                      />
                    )}
                    <div>
                      <div className={styles.login_phoneNumber}>
                        <div className={styles.mb16}>
                          <PhoneNumberForm
                            value={phoneNumber}
                            formField={formField}
                            onChange={setPhoneNumber}
                            hasError={checkError}
                          />
                        </div>
                        <div className={styles.radio_container}>
                          <div className={styles.radio_container}>
                            <p className={styles.subtitle2}>
                              {t("lbl_send_code_via")}
                            </p>
                          </div>
                          <div className={styles.radio_container}>
                            <div className={styles.ml2}>
                              <RadioButton
                                checked={otpProvider === "SMS"}
                                onClick={() => setOtpProvider("SMS")}
                              />
                            </div>

                            <p className={styles.p2}>
                              {t("lbl_text_message_to_the_phone_number")}
                            </p>
                          </div>
                          <div className={styles.radio_container}>
                            <div className={styles.ml2}>
                              <RadioButton
                                checked={otpProvider === "WHATSAPP"}
                                onClick={() => setOtpProvider("WHATSAPP")}
                              />
                            </div>
                            <p className={styles.p2}>
                              {t("lbl_via_the_WhatsApp_application")}
                            </p>
                          </div>
                        </div>
                        {loginBtn()}
                      </div>
                    </div>
                  </>
                </span>
              </>
            ) : (
              loginBtn()
            )}
          </div>
        </Form>
      </Formik>
    </div>
  );
};

export default LoginForm;
